import 'react-big-calendar/lib/css/react-big-calendar.css';
import { get, flatten, isArray } from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { compose, lifecycle, withState, withHandlers, withPropsOnChange } from 'recompose';
import BigCalendar from 'react-big-calendar';
import { breadcrumbs } from '@inqool/uas-web-core/es/utils';

import { withNotifications } from '@components/hoc/withNotifications';
import { getAllDayPlans } from '@modules/hr-dayplans/_dayplansActions';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { Loader } from '@components/loader';
import { periodTypeLabel } from '@utils/utils';
import { getEmployee } from '@modules/employees/_employeesActions';
import 'moment/locale/cs';
import { Dialog } from 'material-ui';
import PeriodForm from '@modules/hr-employee-detail/PeriodForm';
import * as T from './_employeeDetailTypes';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

moment.locale('cs');
BigCalendar.setLocalizer(BigCalendar.momentLocalizer(moment));

const enhance = compose(
  withRouter,
  withNotifications({ getEmployee }),
  breadcrumbs([{ label: 'Detail uživatele' }]),
  withState('periods', 'fetchPeriods', []),
  withState('dayPlans', 'fetchDayplans', []),
  withState('employee', 'fetchEmployee', null),
  withState('isFetching', 'toggleFetching', true),
  withState('dialogOpened', 'toggleDialog', false),
  withState('editedPeriod', 'setEditedPeriod', null),
  withHandlers({
    fetchAllDayPlans: ({ fetchDayplans, fetchPeriods, match, toggleFetching }) => async () => {
      toggleFetching(true);
      const id = get(match, 'params.id');
      await getAllDayPlans([{ field: 'employee.id', operation: 'EQ', value: id }]).then(
        dayPlans => {
          if (isArray(get(dayPlans, 'items'))) {
            // Store dayPlanId and date relationship
            fetchDayplans(get(dayPlans, 'items').map(dayPlan => ({ id: dayPlan.id, date: dayPlan.visualizedDate })));

            fetchPeriods(
              flatten(
                flatten(
                  get(dayPlans, 'items').map(dayPlan => ({ periods: dayPlan.periods, dayPlanId: dayPlan.id }))
                ).map((periodObject: any) =>
                  periodObject.periods.map(period => ({
                    periodId: period.id,
                    type: period.type,
                    dayPlanId: periodObject.dayPlanId,
                    title: periodTypeLabel(get(period, 'type')),
                    start: new Date(period.from),
                    end: new Date(period.to)
                  }))
                )
              )
            );
          }
        },
        error => console.error(error)
      );
      toggleFetching(false);
    }
  }),
  lifecycle<any, any>({
    async componentDidMount() {
      const id = get(this.props, 'match.params.id');
      if (id) {
        await Promise.all([
          this.props
            .getEmployeeNotify(id)
            .then(response => this.props.fetchEmployee(response), error => console.error(error)),
          this.props.fetchAllDayPlans()
        ]);
        this.props.toggleFetching(false);
      }
    }
  }),
  withPropsOnChange(['location'], ({ location }) => ({
    defaultDate: get(location, 'state.defaultDate', new Date())
  }))
);

const View: React.SFC<T.EmployeeDetail> = ({
  isFetching,
  periods,
  employee,
  localizer,
  dialogOpened,
  toggleDialog,
  editedPeriod,
  setEditedPeriod,
  fetchAllDayPlans,
  dayPlans,
  location,
  defaultDate
}) => (
  <>
    {isFetching && <Loader />}
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <h1>Detail uživatele {get(employee, 'fullName', '')}</h1>
      <div>
        <Button
          label="Zpět na směnu"
          style={{ margin: '0 0 0 16px' }}
          containerElement={<Link to={`/hr/shifts/cycles/detail/${get(location, 'state.shiftCycleId')}`} />}
        />
      </div>
    </div>

    <div style={{ height: `calc(100vh - 180px)` }}>
      <BigCalendar
        events={periods}
        views={['week', 'month']}
        showMultiDayTimes={true}
        defaultDate={defaultDate}
        defaultView="week"
        culture="cs"
        localizer={localizer}
        selectable={true}
        messages={{
          allDay: 'Celý den',
          previous: 'Předchozí',
          next: 'Další',
          today: 'Dnes',
          month: 'Měsíc',
          week: 'Týden',
          day: 'Den',
          agenda: 'Agenda',
          date: 'Datum',
          time: 'Čas',
          event: 'Událost'
        }}
        onSelectEvent={event => {
          setEditedPeriod({ edit: true, event });
          toggleDialog(true);
        }}
        onSelectSlot={slot => {
          setEditedPeriod({ edit: false, slot });
          toggleDialog(true);
        }}
        eventPropGetter={event => ({
          className: 'category-' + event.type,
          style: event.type === 'WORKING_HOURS' ? { background: 'grey', border: 'grey' } : null
        })}
      />
    </div>
    <Dialog
      open={dialogOpened}
      title={get(editedPeriod, 'edit') ? 'Editace intervalu' : 'Vytvoření intervalu'}
      onRequestClose={() => {
        toggleDialog(false);
        setEditedPeriod(null);
      }}
    >
      <PeriodForm
        period={editedPeriod}
        dayPlans={dayPlans}
        fetchAllDayPlans={fetchAllDayPlans}
        toggleDialog={toggleDialog}
      />
    </Dialog>
  </>
);

export const EmployeeDetail = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/hr-employee-detail/EmployeeDetail.tsx